import { Component } from '@theme/component';
import { sectionRenderer, sectionIdFor } from '@theme/section-renderer';
import { VariantUpdateEvent } from '@theme/events';

/**
 * Option selection for a product.
 *
 * Picking an option re-renders the product section server side with the new
 * `?variant=` rather than patching price, availability, SKU and buy button
 * from JavaScript. That means the Liquid that rendered the page is the only
 * implementation of any of it: there is no second, client-side copy of the
 * price formatting or the sold-out rules to fall out of step, and anything
 * added to the section later updates on variant change for free.
 *
 * @extends {Component}
 */
class VariantPickerComponent extends Component {
  /**
   * @param {Event} event
   */
  optionChanged = (event) => {
    const input = /** @type {HTMLInputElement | HTMLSelectElement} */ (event.target);
    if (!input.name) return;

    const variant = this.#resolve();

    // A combination with no variant behind it still updates the URL and the
    // section, so the server can render its own unavailable state rather than
    // the theme guessing at one.
    const url = new URL(window.location.href);
    if (variant) {
      url.searchParams.set('variant', String(variant.id));
    } else {
      url.searchParams.delete('variant');
    }

    // replaceState, not pushState: stepping through every option a shopper
    // tried is not a browsing history anyone wants to walk back through.
    window.history.replaceState({}, '', url);

    this.dispatchEvent(
      new VariantUpdateEvent({
        variant,
        productUrl: url.toString(),
        sectionId: sectionIdFor(this) ?? '',
      })
    );

    this.#render(url);
  };

  /**
   * Finds the variant matching the currently selected option values.
   *
   * @returns {object | null}
   */
  #resolve() {
    const selected = this.selectedOptions;
    if (!selected.length) return null;

    return (
      this.variants.find((variant) =>
        variant.options.every((/** @type {string} */ value, /** @type {number} */ index) => value === selected[index])
      ) ?? null
    );
  }

  /** @returns {string[]} Selected value per option, in option order. */
  get selectedOptions() {
    /** @type {string[]} */
    const values = [];

    for (const fieldset of this.querySelectorAll('[data-option-index]')) {
      const index = Number(fieldset.getAttribute('data-option-index'));
      const checked = fieldset.querySelector('input:checked, select');

      if (checked instanceof HTMLInputElement) values[index] = checked.value;
      else if (checked instanceof HTMLSelectElement) values[index] = checked.value;
    }

    return values;
  }

  /** @returns {any[]} */
  get variants() {
    if (this.#variants) return this.#variants;

    const script = this.querySelector('script[type="application/json"]');
    try {
      this.#variants = JSON.parse(script?.textContent ?? '[]');
    } catch {
      this.#variants = [];
    }

    return this.#variants ?? [];
  }

  /** @type {any[] | undefined} */
  #variants;

  /**
   * @param {URL} url
   */
  async #render(url) {
    const sectionId = sectionIdFor(this);
    if (!sectionId) return;

    this.setAttribute('loading', '');

    try {
      await sectionRenderer.render(sectionId, { url });
    } catch (error) {
      console.warn('[variant-picker]', error);
    } finally {
      this.removeAttribute('loading');
    }
  }
}

if (!customElements.get('variant-picker-component')) {
  customElements.define('variant-picker-component', VariantPickerComponent);
}
